import React, { useState, useRef, useEffect } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { getDatabase, ref, get, onValue, set } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import ResumeUploader from '../components/ResumeUploader';
import { Bar, Radar } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  RadialLinearScale, 
  PointElement, 
  LineElement, 
  Filler, 
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import ReactMarkdown from 'react-markdown';

ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  RadialLinearScale, 
  PointElement, 
  LineElement, 
  Filler, 
  Title, 
  Tooltip, 
  Legend
);

const PromptButton = ({ text, onClick, disabled }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 20px',
        fontSize: '13px',
        fontWeight: '600',
        fontFamily: 'Outfit, sans-serif',
        border: '1px solid rgba(103, 58, 183, 0.25)',
        borderRadius: '24px',
        backgroundColor: disabled ? 'rgba(0, 0, 0, 0.02)' : 'rgba(103, 58, 183, 0.08)',
        color: disabled ? '#94a3b8' : '#673ab7',
        cursor: disabled ? 'not-allowed' : 'pointer',
        margin: '5px',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: disabled ? 'none' : '0 2px 6px rgba(103, 58, 183, 0.05)',
      }}
      onMouseOver={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'rgba(103, 58, 183, 0.15)';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.borderColor = 'rgba(103, 58, 183, 0.6)';
        }
      }}
      onMouseOut={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'rgba(103, 58, 183, 0.08)';
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.borderColor = 'rgba(103, 58, 183, 0.25)';
        }
      }}
    >
      {text}
    </button>
);


function Chatbot() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatContainerRef = useRef(null);
  
  const [resumeSummary, setResumeSummary] = useState('');
  const [resumeAnalysis, setResumeAnalysis] = useState(null);
  const [rawProfile, setRawProfile] = useState(null);
  const [compressedSummary, setCompressedSummary] = useState('');
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [isGeneratingCourses, setIsGeneratingCourses] = useState(false);
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' or 'analysis'
  const [currentUser, setCurrentUser] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [proExpiryDate, setProExpiryDate] = useState(null);
  const [chartType, setChartType] = useState('radar'); // 'radar' or 'bar'
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileDetailsOpen, setIsProfileDetailsOpen] = useState(false);
  const [suggestedGoals, setSuggestedGoals] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [customGoal, setCustomGoal] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [goalCourses, setGoalCourses] = useState([]);
  const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setIsLoading(false);
        setIsPro(false);
        setProExpiryDate(null);
        setResumeSummary('');
        setResumeAnalysis(null);
        setSuggestedGoals([]);
        setSelectedGoal('');
        setCustomGoal('');
        setMilestones([]);
        setGoalCourses([]);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const db = getDatabase();
    const resumeRef = ref(db, `resumeAnalysis/${currentUser.uid}`);
    get(resumeRef).then((snapshot) => {
      if (snapshot.exists()) {
        const savedData = snapshot.val();
        setResumeSummary(savedData.summary);
        setResumeAnalysis(savedData.analysis);
        setRawProfile(savedData.rawProfile || null);
        setCompressedSummary(savedData.compressedSummary || '');
        setRecommendedCourses(savedData.recommendedCourses || []);
        setSuggestedGoals(savedData.suggestedGoals || []);
        if (savedData.goalPlan) {
          setSelectedGoal(savedData.goalPlan.goal || '');
          setCustomGoal(savedData.goalPlan.goal || '');
          setMilestones(savedData.goalPlan.milestones || []);
          setGoalCourses(savedData.goalPlan.recommendedCourses || []);
        } else {
          setSelectedGoal('');
          setCustomGoal('');
          setMilestones([]);
          setGoalCourses([]);
        }
      }
    }).catch((error) => console.error("Error fetching resume data:", error));

    const userProfileRef = ref(db, `users/${currentUser.uid}`);
    const unsubscribeProfile = onValue(userProfileRef, (snapshot) => {
      if (snapshot.exists()) {
        const profileData = snapshot.val();
        const expiryDateStr = profileData.proExpiryDate;

        if (expiryDateStr && new Date(expiryDateStr) > new Date()) {
          setIsPro(true);
          setProExpiryDate(expiryDateStr);
        } else {
          setIsPro(false);
          setProExpiryDate(null);
        }
      } else {
        setIsPro(false);
        setProExpiryDate(null);
      }
      setIsLoading(false);
    });
    
    return () => unsubscribeProfile();
  }, [currentUser]);

  useEffect(() => {
    if (chatContainerRef.current) {
      const { scrollHeight, clientHeight } = chatContainerRef.current;
      chatContainerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, [messages]);

  const sendMessage = async (messageContent, displayContent) => {
    if (!messageContent.trim()) return;
    const userMessage = { role: 'user', content: displayContent || messageContent };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setLoading(true);
    let payloadMessages = [...messages, { role: 'user', content: messageContent }];
    const contextText = compressedSummary || resumeSummary;
    if (contextText) {
      const summaryText = Array.isArray(contextText) ? contextText.join('\n') : contextText;
      const modifiedLastMessage = { ...payloadMessages[payloadMessages.length - 1] };
      modifiedLastMessage.content = `Based on the following resume summary, please answer the user's question. IMPORTANT: Only respond to questions related to business or careers, professional or business development, or education. If the question is off-topic, politely state that you can only assist with business or career-related queries.---RESUME SUMMARY: ${summaryText}---USER'S QUESTION: ${messageContent}`;
      payloadMessages[payloadMessages.length - 1] = modifiedLastMessage;
    }
    try {
      const res = await fetch('https://coursehub-282e6.uc.r.appspot.com/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: payloadMessages }),
      });
      const data = await res.json();
      const assistantMessage = { role: 'assistant', content: (data && data.reply) ? data.reply : '❌ Empty or malformed reply from server.' };
      setMessages(prevMessages => [...prevMessages, assistantMessage]);
    } catch (err) {
      const errorMessage = { role: 'assistant', content: '❌ Error contacting GuideAI server.' };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    }
    setLoading(false);
  };

  const handleManualSend = () => {
    sendMessage(input);
    setInput('');
  };

  const sendPredefinedPrompt = (timeframe) => {
    const fullPrompt = `Based on my resume, create a high-quality, 4-point action plan to guide me for the next ${timeframe}. Result is in 4 bullet points.`;
    const displayPrompt = `Give me action plan for the next ${timeframe}.`;
    sendMessage(fullPrompt, displayPrompt);
  };

  const handleLogout = () => {
    signOut(auth).then(() => navigate('/'));
  };

  const handleUploadComplete = (data) => {
    if (currentUser && data && data.summary && data.analysis) {
      setResumeSummary(data.summary);
      setResumeAnalysis(data.analysis);
      setRawProfile(data.rawProfile || null);
      setCompressedSummary(data.compressedSummary || '');
      setRecommendedCourses(data.recommendedCourses || []);
      setSuggestedGoals(data.suggestedGoals || []);
      setSelectedGoal('');
      setCustomGoal('');
      setMilestones([]);
      setGoalCourses([]);
      const dbRef = ref(getDatabase(), `resumeAnalysis/${currentUser.uid}`);
      set(dbRef, data);
    }
  };

  const handleGenerateRoadmap = async (targetGoal) => {
    const goalToUse = (targetGoal || customGoal || selectedGoal || '').trim();
    if (!currentUser || !resumeSummary || !resumeAnalysis || !goalToUse) {
      alert("Please define or select a career goal first.");
      return;
    }
    setIsGeneratingRoadmap(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL 
        ? `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/generate-goal-roadmap`
        : 'https://coursehub-282e6.uc.r.appspot.com/api/generate-goal-roadmap';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary: resumeSummary,
          analysis: {
            technical_skills: resumeAnalysis.technical_skills || 0,
            design_ux_ui: resumeAnalysis.design_ux_ui || 0,
            product_project_mgmt: resumeAnalysis.product_project_mgmt || 0,
            leadership_people_mgmt: resumeAnalysis.leadership_people_mgmt || 0,
            data_analytics: resumeAnalysis.data_analytics || 0,
            communication_soft_skills: resumeAnalysis.communication_soft_skills || 0
          },
          goal: goalToUse
        })
      });
      
      const result = await response.json();
      if (response.ok && result.success) {
        setMilestones(result.milestones || []);
        setGoalCourses(result.recommendedCourses || []);
        setSelectedGoal(goalToUse);
        setCustomGoal(goalToUse);

        // Save updated goalPlan to RTDB
        const dbRef = ref(getDatabase(), `resumeAnalysis/${currentUser.uid}`);
        const snap = await get(dbRef);
        if (snap.exists()) {
          const currentData = snap.val();
          currentData.goalPlan = {
            goal: goalToUse,
            milestones: result.milestones || [],
            recommendedCourses: result.recommendedCourses || []
          };
          await set(dbRef, currentData);
        }
      } else {
        alert(result.message || 'Failed to generate goal roadmap.');
      }
    } catch (err) {
      console.error("Error generating goal roadmap:", err);
      alert("Failed to generate goal roadmap due to a network or server error.");
    } finally {
      setIsGeneratingRoadmap(false);
    }
  };

  const handleGenerateCourses = async () => {
    if (!currentUser || !resumeSummary || !resumeAnalysis) return;
    setIsGeneratingCourses(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://coursehub-282e6.uc.r.appspot.com/api/recommend-courses';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary: resumeSummary, analysis: resumeAnalysis }),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        const courses = result.recommendedCourses || [];
        setRecommendedCourses(courses);
        
        // Save the updated object to RTDB
        const dbRef = ref(getDatabase(), `resumeAnalysis/${currentUser.uid}`);
        const snap = await get(dbRef);
        if (snap.exists()) {
          const currentData = snap.val();
          currentData.recommendedCourses = courses;
          await set(dbRef, currentData);
        } else {
          await set(dbRef, {
            summary: resumeSummary,
            analysis: resumeAnalysis,
            rawProfile: rawProfile,
            compressedSummary: compressedSummary,
            recommendedCourses: courses
          });
        }
      } else {
        alert(result.message || 'Failed to generate course recommendations.');
      }
    } catch (err) {
      console.error('Failed to generate course recommendations:', err);
      alert('Error connecting to backend to generate recommendations.');
    }
    setIsGeneratingCourses(false);
  };

  // Transform keys to Title Case for nice labels
  const formatLabel = (key) => {
    const customLabels = {
      technical_skills: 'Technical Skills',
      design_ux_ui: 'Design & UX/UI',
      product_project_mgmt: 'Product & Project',
      leadership_people_mgmt: 'Leadership & People',
      data_analytics: 'Data & Analytics',
      communication_soft_skills: 'Communication & Soft'
    };
    if (customLabels[key]) return customLabels[key];

    return key
      .split(/[_-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Chart configuration options for premium light mode
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { 
        display: true, 
        text: 'Professional Skills Analysis', 
        color: '#1e293b',
        font: { size: 16, weight: '700', family: 'Outfit, sans-serif' } 
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1e293b',
        bodyColor: '#334155',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        titleFont: { family: 'Outfit, sans-serif', weight: '700' },
        bodyFont: { family: 'Outfit, sans-serif' }
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { color: '#475569', font: { family: 'Outfit, sans-serif', size: 10 } }
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { color: '#475569', font: { family: 'Outfit, sans-serif' } },
        title: { display: true, text: 'Score (0-100)', color: '#475569', font: { family: 'Outfit, sans-serif' } }
      }
    },
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { 
        display: true, 
        text: 'Professional Skills Profile', 
        color: '#1e293b',
        font: { size: 16, weight: '700', family: 'Outfit, sans-serif' } 
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1e293b',
        bodyColor: '#334155',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        titleFont: { family: 'Outfit, sans-serif', weight: '700' },
        bodyFont: { family: 'Outfit, sans-serif' }
      }
    },
    scales: {
      r: {
        grid: { color: 'rgba(0, 0, 0, 0.06)' },
        angleLines: { color: 'rgba(0, 0, 0, 0.06)' },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: { 
          stepSize: 20, 
          color: '#475569', 
          backdropColor: 'transparent',
          font: { family: 'Outfit, sans-serif', size: 9 } 
        },
        pointLabels: {
          color: '#1e293b',
          font: { family: 'Outfit, sans-serif', size: 10, weight: '600' }
        }
      }
    }
  };

  const chartLabels = resumeAnalysis ? Object.keys(resumeAnalysis).map(formatLabel) : [];
  const chartValues = resumeAnalysis ? Object.values(resumeAnalysis) : [];

  const chartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Score',
      data: chartValues,
      backgroundColor: chartType === 'radar' 
        ? 'rgba(103, 58, 183, 0.15)'  // Light purple fill
        : [
            'rgba(103, 58, 183, 0.75)',  // Purple
            'rgba(236, 72, 153, 0.75)',  // Pink
            'rgba(168, 85, 247, 0.75)',  // Purple-pink
            'rgba(16, 185, 129, 0.75)',  // Green
            'rgba(245, 158, 11, 0.75)',  // Amber
            'rgba(6, 182, 212, 0.75)',   // Cyan
          ],
      borderColor: chartType === 'radar'
        ? '#673ab7'
        : [
            '#673ab7',
            '#ec4899',
            '#a855f7',
            '#10b981',
            '#f59e0b',
            '#06b6d4',
          ],
      borderWidth: 2,
      pointBackgroundColor: '#673ab7',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: '#673ab7',
    }],
  };


  return (
    <div style={{ 
      fontFamily: 'Outfit, sans-serif', 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh', 
      color: '#1e293b',
      display: 'flex',
      flexDirection: 'column'
    }}>
      
      {/* Premium Light Header */}
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 40px',
        borderBottom: '1px solid #e2e8f0',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(12px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '24px' }}>🎓</span>
          <h1 style={{ 
            margin: 0, 
            fontSize: '20px', 
            fontWeight: '700', 
            letterSpacing: '-0.5px',
            color: '#673ab7',
          }}>
            GuideAI Chatbot
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {isPro && proExpiryDate ? (
            <div style={{ 
              borderRadius: '24px', 
              padding: '6px 14px', 
              backgroundColor: '#e9f7ef', 
              border: '2px solid #28a745',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{ color: '#28a745', fontSize: '14px' }}>●</span>
              <span style={{ fontSize: '13px', color: '#155724', fontWeight: '600' }}>
                Pro Member (Expires {new Date(proExpiryDate).toLocaleDateString()})
              </span>
            </div>
          ) : (
            <button 
              onClick={() => navigate('/upgrade')}
              style={{ 
                padding: '8px 18px', 
                fontSize: '14px', 
                border: 'none', 
                borderRadius: '20px', 
                backgroundColor: '#ffc107', 
                color: '#212529', 
                fontWeight: '700', 
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 193, 7, 0.25)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.backgroundColor = '#e0a800';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.backgroundColor = '#ffc107';
              }}
            >
              ✨ Upgrade to Pro
            </button>
          )}

          <button 
            onClick={() => navigate('/dream')}
            style={{ 
              padding: '8px 18px', 
              fontSize: '14px', 
              border: '1px solid #673ab7', 
              borderRadius: '20px', 
              backgroundColor: '#f3e8ff', 
              color: '#673ab7',
              fontWeight: '700', 
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginRight: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#e9d5ff';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#f3e8ff';
            }}
          >
            ⚡ Career Simulator
          </button>

          <button 
            onClick={handleLogout} 
            style={{ 
              padding: '8px 16px', 
              fontSize: '14px', 
              border: '1px solid rgba(239, 68, 68, 0.3)', 
              borderRadius: '20px', 
              backgroundColor: 'rgba(239, 68, 68, 0.04)', 
              color: '#dc2626',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.04)'}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content Body */}
      <main style={{ flex: 1, padding: '30px 40px', maxWidth: '1400px', width: '95%', margin: '0 auto' }}>
        
        {/* If user is Pro but hasn't uploaded a resume yet, show uploader centered */}
        {isPro && !resumeAnalysis && (
          <div style={{
            maxWidth: '700px',
            margin: '60px auto',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '10px', color: '#1e293b' }}>Upload Your Resume</h2>
            <p style={{ color: '#64748b', marginBottom: '30px' }}>Let's analyze your skills to build a customized profile and start your career guidance journey.</p>
            <ResumeUploader onUploadComplete={handleUploadComplete} />
          </div>
        )}

        {/* If user is NOT Pro, show upgrade card */}
        {!isPro && (
          <div style={{ 
            padding: '40px', 
            border: '2px dashed #ffc107', 
            borderRadius: '16px', 
            textAlign: 'center', 
            backgroundColor: '#fffbeb', 
            maxWidth: '650px', 
            margin: '80px auto',
            boxShadow: '0 4px 20px rgba(255, 193, 7, 0.05)'
          }}>
            <span style={{ fontSize: '40px' }}>✨</span>
            <h3 style={{ fontSize: '22px', fontWeight: '700', margin: '15px 0 10px 0', color: '#1e293b' }}>Unlock Full Potential with Pro</h3>
            <p style={{ color: '#475569', lineHeight: '1.6', maxWidth: '480px', margin: '0 auto 25px auto' }}>
              Upload your resume to get a detailed multi-dimensional skill analysis, visual interactive graphs, and personalized career mentoring.
            </p>
            <button 
              onClick={() => navigate('/upgrade')} 
              style={{
                padding: '12px 28px', 
                fontSize: '16px', 
                border: 'none', 
                borderRadius: '24px', 
                backgroundColor: '#ffc107', 
                color: '#212529', 
                cursor: 'pointer', 
                fontWeight: '700',
                boxShadow: '0 4px 14px rgba(255, 193, 7, 0.3)'
              }}
            >
              Upgrade Now
            </button>
          </div>
        )}

        {/* Loader while checking state */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '100px 0', color: '#475569' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '3px solid rgba(0,0,0,0.05)',
              borderTopColor: '#673ab7',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '15px'
            }} />
            <p style={{ fontSize: '16px' }}>Loading your profile data...</p>
          </div>
        ) : resumeAnalysis && isPro ? (
          
          /* TWO-COLUMN Grid Layout */
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1.2fr 1fr', 
            gap: '35px',
            alignItems: 'start',
            marginTop: '10px'
          }}>
            
            {/* Left Column: Uploader, Data Analysis & Graph */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              
              {/* Uploader Card (Always accessible for Pro users to re-upload) */}
              <div style={{ 
                background: '#ffffff', 
                border: '1px solid #e2e8f0', 
                borderRadius: '16px', 
                padding: '20px 25px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
              }}>
                <h3 style={{ 
                  fontSize: '15px', 
                  fontWeight: '700', 
                  margin: '0 0 15px 0', 
                  color: '#673ab7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📤</span> Upload Resume
                </h3>
                <ResumeUploader onUploadComplete={handleUploadComplete} />
              </div>

              {/* Summary Card */}
              <div style={{ 
                background: '#ffffff', 
                border: '1px solid #e2e8f0', 
                borderRadius: '16px', 
                padding: '25px 30px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
              }}>
                <h2 style={{ 
                  fontSize: '17px', 
                  fontWeight: '700', 
                  marginBottom: '15px', 
                  borderBottom: '1px solid #e2e8f0',
                  paddingBottom: '10px',
                  color: '#673ab7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📄</span> Profile Summary
                </h2>
                <div style={{ 
                  fontSize: '14.5px',
                  color: '#334155',
                  lineHeight: '1.7',
                  margin: 0
                }}>
                  {Array.isArray(resumeSummary) ? (
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {resumeSummary.map((bullet, idx) => (
                        <li key={idx} style={{ marginBottom: '8px' }}>{bullet}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{resumeSummary}</p>
                  )}
                </div>
              </div>

              {rawProfile && (
                <div style={{ 
                  background: '#ffffff', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '16px', 
                  padding: '25px 30px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <div 
                    onClick={() => setIsProfileDetailsOpen(!isProfileDetailsOpen)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    <h2 style={{ 
                      fontSize: '17px', 
                      fontWeight: '700', 
                      color: '#673ab7',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      margin: 0
                    }}>
                      <span>🔗</span> Imported Profile Details
                    </h2>
                    <span style={{ 
                      fontSize: '14px',
                      color: '#64748b',
                      transform: isProfileDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      fontWeight: 'bold'
                    }}>
                      ▼
                    </span>
                  </div>

                  {isProfileDetailsOpen && (
                    <div style={{
                      marginTop: '20px',
                      borderTop: '1px solid #f1f5f9',
                      paddingTop: '15px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                      fontSize: '14px',
                      color: '#334155'
                    }}>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>
                          {rawProfile.fullName}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '13.5px', marginTop: '4px', fontWeight: '500' }}>
                          {rawProfile.headline}
                        </div>
                      </div>

                      {rawProfile.summary && (
                        <div>
                          <div style={{ fontWeight: '700', color: '#673ab7', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                            About
                          </div>
                          <div style={{ lineHeight: '1.6', color: '#475569' }}>
                            {rawProfile.summary}
                          </div>
                        </div>
                      )}

                      {rawProfile.experiences && rawProfile.experiences.length > 0 && (
                        <div>
                          <div style={{ fontWeight: '700', color: '#673ab7', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                            Experience
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {rawProfile.experiences.map((exp, idx) => (
                              <div key={idx} style={{ borderLeft: '2px solid rgba(103, 58, 183, 0.15)', paddingLeft: '12px' }}>
                                <div style={{ fontWeight: '600', color: '#1e293b' }}>{exp.title}</div>
                                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                                  {exp.company} {exp.starts_at && `• ${exp.starts_at} - ${exp.ends_at || 'Present'}`}
                                </div>
                                {exp.description && (
                                  <div style={{ fontSize: '13px', color: '#475569', marginTop: '6px', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                                    {exp.description}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {rawProfile.education && rawProfile.education.length > 0 && (
                        <div>
                          <div style={{ fontWeight: '700', color: '#673ab7', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                            Education
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {rawProfile.education.map((edu, idx) => (
                              <div key={idx} style={{ borderLeft: '2px solid rgba(103, 58, 183, 0.15)', paddingLeft: '12px' }}>
                                <div style={{ fontWeight: '600', color: '#1e293b' }}>{edu.school}</div>
                                <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>
                                  {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {rawProfile.skills && rawProfile.skills.length > 0 && (
                        <div>
                          <div style={{ fontWeight: '700', color: '#673ab7', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                            Skills
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {rawProfile.skills.map((skill, idx) => (
                              <span 
                                key={idx} 
                                style={{
                                  backgroundColor: 'rgba(103, 58, 183, 0.08)',
                                  color: '#673ab7',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Chart Card */}
              <div style={{ 
                background: '#ffffff', 
                border: '1px solid #e2e8f0', 
                borderRadius: '16px', 
                padding: '25px 30px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px',
                  borderBottom: '1px solid #e2e8f0',
                  paddingBottom: '10px'
                }}>
                  <h2 style={{ 
                    fontSize: '17px', 
                    fontWeight: '700', 
                    margin: 0,
                    color: '#673ab7',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>📊</span> Skills Visualization
                  </h2>
                  
                  {/* Selector Toggle */}
                  <div style={{ 
                    display: 'flex', 
                    backgroundColor: '#f1f5f9', 
                    borderRadius: '20px', 
                    padding: '2px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <button 
                      onClick={() => setChartType('radar')}
                      style={{
                        padding: '6px 14px',
                        fontSize: '12px',
                        fontWeight: '600',
                        fontFamily: 'Outfit, sans-serif',
                        borderRadius: '18px',
                        border: 'none',
                        backgroundColor: chartType === 'radar' ? '#673ab7' : 'transparent',
                        color: chartType === 'radar' ? '#ffffff' : '#64748b',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Radar
                    </button>
                    <button 
                      onClick={() => setChartType('bar')}
                      style={{
                        padding: '6px 14px',
                        fontSize: '12px',
                        fontWeight: '600',
                        fontFamily: 'Outfit, sans-serif',
                        borderRadius: '18px',
                        border: 'none',
                        backgroundColor: chartType === 'bar' ? '#673ab7' : 'transparent',
                        color: chartType === 'bar' ? '#ffffff' : '#64748b',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Bar
                    </button>
                  </div>
                </div>

                <div style={{ height: '360px', position: 'relative', padding: '10px 0' }}>
                   {chartType === 'radar' ? (
                     <Radar options={radarOptions} data={chartData} />
                   ) : (
                     <Bar options={barOptions} data={chartData} />
                   )}
                </div>
              </div>

              {/* Target Goal & Career Roadmap Card */}
              <div style={{ 
                background: '#ffffff', 
                border: '1px solid #e2e8f0', 
                borderRadius: '16px', 
                padding: '25px 30px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                <h2 style={{ 
                  fontSize: '17px', 
                  fontWeight: '700', 
                  color: '#673ab7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: 0
                }}>
                  <span>🎯</span> Career Target & Custom Roadmap
                </h2>

                {/* Suggested Goals */}
                {suggestedGoals && suggestedGoals.length > 0 && (
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '8px' }}>
                      Suggested Career Goals:
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {suggestedGoals.map((g, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setCustomGoal(g);
                            handleGenerateRoadmap(g);
                          }}
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '20px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#475569',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = '#e2e8f0';
                            e.currentTarget.style.borderColor = '#94a3b8';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = '#f1f5f9';
                            e.currentTarget.style.borderColor = '#cbd5e1';
                          }}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Goal Input Field */}
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '8px' }}>
                    Define Your Target Goal:
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="text"
                      value={customGoal}
                      onChange={(e) => setCustomGoal(e.target.value)}
                      placeholder="e.g., Become a Staff Engineer, Transition to Product Manager..."
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13.5px',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#673ab7'}
                      onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                    />
                    <button
                      onClick={() => handleGenerateRoadmap(customGoal)}
                      disabled={isGeneratingRoadmap || !customGoal.trim()}
                      style={{
                        background: '#673ab7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0 16px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s',
                        opacity: (!customGoal.trim() || isGeneratingRoadmap) ? 0.6 : 1
                      }}
                    >
                      {isGeneratingRoadmap ? 'Generating...' : 'Generate Plan'}
                    </button>
                  </div>
                </div>

                {/* Milestones Plan (Timeline) */}
                {milestones && milestones.length > 0 && (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px', marginTop: '5px' }}>
                    <h3 style={{ fontSize: '14.5px', fontWeight: '700', color: '#1e293b', margin: '0 0 15px 0' }}>
                      🏁 Step-by-Step Milestones
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative', paddingLeft: '20px', borderLeft: '2px solid #e2e8f0' }}>
                      {milestones.map((m, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          {/* Circle marker */}
                          <div style={{
                            position: 'absolute',
                            left: '-27px',
                            top: '2px',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: '#673ab7',
                            border: '3px solid #ffffff',
                            boxShadow: '0 0 0 2px #673ab7'
                          }} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>
                                {m.title}
                              </h4>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#673ab7', background: '#f3e8ff', padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
                                {m.timeframe}
                              </span>
                            </div>
                            <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: '1.45' }}>
                              {m.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Recommended Courses Card */}
              <div style={{ 
                background: '#ffffff', 
                border: '1px solid #e2e8f0', 
                borderRadius: '16px', 
                padding: '25px 30px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
              }}>
                <h2 style={{ 
                  fontSize: '17px', 
                  fontWeight: '700', 
                  marginBottom: '15px', 
                  borderBottom: '1px solid #e2e8f0',
                  paddingBottom: '10px',
                  color: '#673ab7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: 0
                }}>
                  <span>🎓</span> {selectedGoal ? 'Goal-Targeted Courses' : 'Recommended Courses'}
                </h2>
                
                {((goalCourses && goalCourses.length > 0) ? goalCourses : recommendedCourses) && ((goalCourses && goalCourses.length > 0) ? goalCourses : recommendedCourses).length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {((goalCourses && goalCourses.length > 0) ? goalCourses : recommendedCourses).map((course, idx) => (
                      <div key={idx} style={{ 
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
                            {course.title}
                          </h4>
                          <span style={{ 
                            fontSize: '11px',
                            fontWeight: '700',
                            backgroundColor: course.difficulty === 'Advanced' ? '#fee2e2' : course.difficulty === 'Intermediate' ? '#fef3c7' : '#dcfce7',
                            color: course.difficulty === 'Advanced' ? '#991b1b' : course.difficulty === 'Intermediate' ? '#92400e' : '#166534',
                            padding: '3px 8px',
                            borderRadius: '10px',
                            textTransform: 'uppercase'
                          }}>
                            {course.difficulty}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#673ab7' }}>
                          Provided by {course.provider}
                        </div>
                        <p style={{ margin: 0, fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                          {course.description}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '4px' }}>
                          {course.skillsAddressed && course.skillsAddressed.map((skill, sIdx) => (
                            <span key={sIdx} style={{ 
                              fontSize: '11px', 
                              backgroundColor: '#e2e8f0', 
                              color: '#475569', 
                              padding: '2px 8px', 
                              borderRadius: '6px',
                              fontWeight: '500'
                            }}>
                              {skill}
                            </span>
                          ))}
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <a 
                            href={`https://www.google.com/search?q=${encodeURIComponent(course.title + ' ' + course.provider)}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#673ab7',
                              textDecoration: 'none',
                              borderBottom: '1px solid transparent',
                              transition: 'border-color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.borderBottomColor = '#673ab7'}
                            onMouseOut={(e) => e.currentTarget.style.borderBottomColor = 'transparent'}
                          >
                            Explore Course ↗
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '15px 0' }}>
                    <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#64748b' }}>
                      No course recommendations found for your profile yet.
                    </p>
                    <button
                      onClick={handleGenerateCourses}
                      disabled={isGeneratingCourses}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '700',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: '#673ab7',
                        color: 'white',
                        cursor: isGeneratingCourses ? 'not-allowed' : 'pointer',
                        opacity: isGeneratingCourses ? 0.6 : 1,
                        boxShadow: '0 4px 10px rgba(103, 58, 183, 0.2)',
                        transition: 'all 0.2s'
                      }}
                    >
                      {isGeneratingCourses ? 'Generating...' : '⚡ Generate Course Recommendations'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column: Chatbot Interface */}
            <div style={{ 
              background: '#ffffff', 
              border: '1px solid #e2e8f0', 
              borderRadius: '16px',
              height: '780px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
              overflow: 'hidden'
            }}>
              {/* Chat Title Header */}
              <div style={{
                padding: '20px 25px',
                borderBottom: '1px solid #e2e8f0',
                background: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '18px' }}>🤖</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>GuideAI Career Advisor</h3>
                  <span style={{ fontSize: '11px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                    <span style={{ animation: 'pulse 1.5s infinite', color: '#10b981' }}>●</span> Online
                  </span>
                </div>
              </div>

              {/* Chat Messages Log */}
              <div 
                ref={chatContainerRef} 
                style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  padding: '25px', 
                  backgroundColor: '#f8fafc', 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px'
                }}
              >
                {messages.length === 0 ? (
                  <div style={{ 
                    margin: 'auto', 
                    textAlign: 'center', 
                    maxWidth: '300px', 
                    color: '#64748b',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '32px' }}>💬</span>
                    <p style={{ fontSize: '14px', margin: 0 }}>Ask me anything about your resume, career goals, or action plans.</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        display: 'flex', 
                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <div style={{
                        maxWidth: '85%',
                        padding: '12px 18px',
                        borderRadius: '16px',
                        border: msg.role === 'user' ? 'none' : '1px solid #e2e8f0',
                        background: msg.role === 'user' 
                          ? 'linear-gradient(135deg, #673ab7 0%, #512da8 100%)' 
                          : '#ffffff',
                        color: msg.role === 'user' ? '#ffffff' : '#1e293b',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                        wordBreak: 'break-word'
                      }}>
                        <div style={{ 
                          fontSize: '10px', 
                          color: msg.role === 'user' ? '#e9d5ff' : '#64748b', 
                          marginBottom: '4px', 
                          fontWeight: '700', 
                          letterSpacing: '0.5px' 
                        }}>
                          {msg.role === 'user' ? 'YOU' : 'GUIDEAI'}
                        </div>
                        <div style={{ fontSize: '13.5px', lineHeight: '1.6' }} className="markdown-content">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {loading && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{
                      padding: '12px 18px',
                      borderRadius: '16px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <div style={{ width: '6px', height: '6px', backgroundColor: '#64748b', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both' }} />
                      <div style={{ width: '6px', height: '6px', backgroundColor: '#64748b', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.2s' }} />
                      <div style={{ width: '6px', height: '6px', backgroundColor: '#64748b', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.4s' }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Predefined prompts pills */}
              <div style={{ 
                padding: '10px 15px', 
                borderTop: '1px solid #e2e8f0',
                background: '#fafafa',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '2px'
              }}>
                <PromptButton text="📅 1-Month Plan" onClick={() => sendPredefinedPrompt('1 month')} disabled={loading} />
                <PromptButton text="🎯 3-Month Plan" onClick={() => sendPredefinedPrompt('3 months')} disabled={loading} />
                <PromptButton text="🚀 6-Month Plan" onClick={() => sendPredefinedPrompt('6 months')} disabled={loading} />
              </div>

              {/* Textarea Input area */}
              <div style={{ padding: '20px', display: 'flex', gap: '10px', alignItems: 'center', background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                <textarea 
                  rows="2" 
                  placeholder="Ask a career follow-up question..." 
                  value={input} 
                  onChange={(e) => setInput(e.target.value)} 
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleManualSend();
                    }
                  }}
                  style={{ 
                    padding: '12px 16px', 
                    fontSize: '14px', 
                    fontFamily: 'Outfit, sans-serif',
                    color: '#1e293b',
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1', 
                    borderRadius: '12px', 
                    flex: 1, 
                    resize: 'none',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#673ab7'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                />
                <button 
                  onClick={handleManualSend} 
                  disabled={loading || !input.trim()} 
                  style={{ 
                    padding: '12px 20px', 
                    fontSize: '14px', 
                    fontWeight: '700',
                    fontFamily: 'Outfit, sans-serif',
                    border: 'none', 
                    borderRadius: '12px', 
                    backgroundColor: loading || !input.trim() ? '#e9d5ff' : '#673ab7', 
                    color: loading || !input.trim() ? '#94a3b8' : '#ffffff', 
                    cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: loading || !input.trim() ? 'none' : '0 4px 12px rgba(103, 58, 183, 0.2)'
                  }}
                >
                  Send
                </button>
              </div>
            </div>
            
          </div>
        ) : (
          isPro && (
            <div style={{ textAlign: 'center', padding: '100px 0', color: '#64748b' }}>
              <p>Please upload your resume to generate the skills profile.</p>
            </div>
          )
        )}
      </main>

      {/* Embedded CSS Animations for Spinner & Bounces */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1.0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .markdown-content p {
          margin: 0 0 10px 0;
        }
        .markdown-content p:last-child {
          margin: 0;
        }
        .markdown-content ul, .markdown-content ol {
          margin: 5px 0;
          padding-left: 20px;
        }
        .markdown-content li {
          margin-bottom: 5px;
        }
      `}</style>
    </div>
  );
}

export default Chatbot;